import React, { useState, useEffect } from 'react';
import { ArrowUpRight, ArrowDownRight, ChevronDown } from 'lucide-react';
import { leadsAPI } from '../services/api';
import { formatCurrency } from '../utils/formatters';

const formatBudgetPKR = (budget) => {
  if (!budget || isNaN(Number(budget))) return 'PKR 0';
  const num = Number(budget);
  if (num >= 10000000) return `PKR ${(num / 10000000).toFixed(1).replace(/\.0$/, '')} Cr`;
  if (num >= 100000) return `PKR ${(num / 100000).toFixed(1).replace(/\.0$/, '')} Lac`;
  return formatCurrency(num);
};

const getDateRanges = (preset, customStart, customEnd) => {
  const now = new Date();
  let currentStart, currentEnd, prevStart, prevEnd;

  currentEnd = now.toISOString().split('T')[0];

  if (preset === 'Today') {
    currentStart = currentEnd;
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    prevStart = prevEnd = yesterday.toISOString().split('T')[0];
  } else if (preset === 'This Week') {
    const weekAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 6);
    currentStart = weekAgo.toISOString().split('T')[0];

    const prevWeekEnd = new Date(weekAgo);
    prevWeekEnd.setDate(weekAgo.getDate() - 1);
    prevEnd = prevWeekEnd.toISOString().split('T')[0];

    const prevWeekStart = new Date(prevWeekEnd);
    prevWeekStart.setDate(prevWeekEnd.getDate() - 6);
    prevStart = prevWeekStart.toISOString().split('T')[0];
  } else if (preset === 'This Month') {
    const monthAgo = new Date(now);
    monthAgo.setDate(now.getDate() - 29);
    currentStart = monthAgo.toISOString().split('T')[0];

    const prevMonthEnd = new Date(monthAgo);
    prevMonthEnd.setDate(monthAgo.getDate() - 1);
    prevEnd = prevMonthEnd.toISOString().split('T')[0];

    const prevMonthStart = new Date(prevMonthEnd);
    prevMonthStart.setDate(prevMonthEnd.getDate() - 29);
    prevStart = prevMonthStart.toISOString().split('T')[0];
  } else if (preset === 'Custom Range' && customStart && customEnd) {
    currentStart = customStart;
    currentEnd = customEnd;
    
    const start = new Date(customStart);
    const end = new Date(customEnd);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    
    const prevEndObj = new Date(start);
    prevEndObj.setDate(start.getDate() - 1);
    prevEnd = prevEndObj.toISOString().split('T')[0];

    const prevStartObj = new Date(prevEndObj);
    prevStartObj.setDate(prevEndObj.getDate() - diffDays);
    prevStart = prevStartObj.toISOString().split('T')[0];
  }

  return { current_start: currentStart, current_end: currentEnd, prev_start: prevStart, prev_end: prevEnd };
};

const VarianceBadge = ({ variance }) => {
  if (variance === 0) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">
        0%
      </span>
    );
  }
  
  const isPositive = variance > 0;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
      {isPositive ? <ArrowUpRight size={14} className="mr-0.5" /> : <ArrowDownRight size={14} className="mr-0.5" />}
      {isPositive ? '+' : ''}{variance}%
    </span>
  );
};

const LeadStatsBar = ({ activeLeadView }) => {
  const [preset, setPreset] = useState('This Week');
  const [showCustom, setShowCustom] = useState(false);
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const sourceMap = {
    general: 'General',
    meta_ads: 'Meta Ads',
    eighteen: 'Eighteen'
  };
  const source = sourceMap[activeLeadView] || null;

  const fetchStats = async (selectedPreset, start, end) => {
    try {
      setLoading(true);
      const ranges = getDateRanges(selectedPreset, start, end);
      if (!ranges.current_start) {
        setLoading(false);
        return; // Wait for complete custom dates
      }
      const response = await leadsAPI.getStatsBar({ ...ranges, source });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats bar:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (preset !== 'Custom Range') {
      setShowCustom(false);
      fetchStats(preset);
    } else if (customRange.start && customRange.end) {
      fetchStats('Custom Range', customRange.start, customRange.end);
    }
  }, [preset, customRange, activeLeadView]);

  return (
    <div className="w-full" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex flex-col gap-2">
        <div className="relative inline-flex items-center self-start">
          <select
            value={preset}
            onChange={(e) => {
              setPreset(e.target.value);
              if (e.target.value === 'Custom Range') setShowCustom(true);
            }}
            className="appearance-none bg-transparent text-sm font-medium text-gray-700 outline-none focus:ring-0 focus:border-transparent cursor-pointer pr-5"
          >
            <option value="Today">Today</option>
            <option value="This Week">Last 7 days</option>
            <option value="This Month">This Month</option>
            <option value="Custom Range">Custom Range</option>
          </select>
          <ChevronDown size={16} className="absolute right-0 text-gray-400 pointer-events-none" />
        </div>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-gray-200">
              {[1, 2, 3, 4].map((k) => (
                <div key={k} className="h-[72px] animate-pulse bg-white/60" />
              ))}
            </div>
          ) : stats ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-gray-200">
              {[
                { title: 'New', value: stats.current?.new_leads || 0, variance: stats.variances?.new_leads || 0 },
                { title: 'Closed', value: stats.current?.closed_leads || 0, variance: stats.variances?.closed_leads || 0 },
                { title: 'Lost', value: stats.current?.lost_leads || 0, variance: stats.variances?.lost_leads || 0 },
                { title: 'Total closed', value: formatBudgetPKR(stats.current?.total_value || 0), variance: stats.variances?.total_value || 0 }
              ].map((item) => (
                <div key={item.title} className="px-5 py-4">
                  <div className="text-xs font-medium text-gray-500 mb-1">{item.title}</div>
                  <div className="flex items-center gap-2">
                    <div className="text-[26px] leading-none font-semibold text-gray-900">{item.value}</div>
                    <VarianceBadge variance={item.variance} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[72px] w-full text-sm text-gray-400">
              Stats unavailable
            </div>
          )}
        </div>

        {showCustom && (
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-md border border-gray-200 self-start">
            <input
              type="date"
              value={customRange.start}
              onChange={(e) => setCustomRange((prev) => ({ ...prev, start: e.target.value }))}
              className="px-1 py-0.5 text-sm text-gray-600 outline-none"
            />
            <span className="text-gray-400">-</span>
            <input
              type="date"
              value={customRange.end}
              onChange={(e) => setCustomRange((prev) => ({ ...prev, end: e.target.value }))}
              className="px-1 py-0.5 text-sm text-gray-600 outline-none"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadStatsBar;
